import { NavLink, Navigate, useLocation } from "react-router-dom";
import { FileText, Wallet, Calculator, Briefcase, Users, Clock, Lock, LayoutDashboard as LayoutDashboardIcon } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import QuickActionsBar from "@/components/dashboard/QuickActionsBar";
import ReceiptReminderBanner from "@/components/ReceiptReminderBanner";
import UpgradeRequiredView from "@/components/UpgradeRequiredView";
import DashboardV2 from "@/pages/DashboardV2";
import DashboardInvoices from "@/pages/dashboard/DashboardInvoices";
import DashboardTaxes from "@/pages/dashboard/DashboardTaxes";
import DashboardProjects from "@/pages/dashboard/DashboardProjects";
import DashboardCustomers from "@/pages/dashboard/DashboardCustomers";
import DashboardTimeEntries from "@/pages/dashboard/DashboardTimeEntries";

const allNavItems = [
  { to: "/dashboard/invoices", label: "Rechnungen", icon: FileText, testId: "dashboard-nav-invoices" },
  { to: "/dashboard/finance", label: "Finanzen", icon: Wallet, testId: "dashboard-nav-finance" },
  { to: "/dashboard/taxes", label: "Steuern", icon: Calculator, testId: "dashboard-nav-taxes", requiresPro: true },
  { to: "/dashboard/projects", label: "Projekte", icon: Briefcase, testId: "dashboard-nav-projects" },
  { to: "/dashboard/customers", label: "Kunden", icon: Users, testId: "dashboard-nav-customers" },
  { to: "/dashboard/time-entries", label: "Zeiteinträge", icon: Clock, testId: "dashboard-nav-time-entries" },
];

function isProPlan(user) {
  if (!user) return false;
  if (user.has_lifetime_access) return true;
  const plan = user.plan;
  return plan === 'pro' || plan === 'pro_monthly' || plan === 'pro_yearly' || plan === 'lifetime';
}

function hasDashboardAccess(user) {
  if (!user) return false;
  if (user.has_lifetime_access) return true;
  return user.plan && user.plan !== 'free';
}

export default function DashboardLayout() {
  const location = useLocation();
  const { user } = useAuth();

  // Free plan: show upgrade prompt (Iter 73i: konsistente UpgradeRequiredView)
  if (!hasDashboardAccess(user)) {
    return (
      <UpgradeRequiredView
        source="dashboard_layout"
        icon={LayoutDashboardIcon}
        title="Dashboard-Übersicht"
        subtitle="Alle Finanzen, Kunden und Projekte auf einen Blick — ab Basic verfügbar."
        features={[
          'Umsatz-, Ausgaben- und Gewinn-KPIs in Echtzeit',
          'Quartalsweise Trendanalyse',
          'Offene Rechnungen und überfällige Posten',
          'Top-Kunden und Top-Projekte',
          'Direkter Zugriff auf alle Module',
        ]}
      />
    );
  }

  if (location.pathname === "/dashboard") {
    return <Navigate to="/dashboard/finance" replace />;
  }

  const isPro = isProPlan(user);

  // If trying to access taxes sub-tab without pro plan, redirect to finance
  if (location.pathname.startsWith("/dashboard/taxes") && !isPro) {
    return <Navigate to="/dashboard/finance" replace />;
  }

  let ViewComponent = DashboardV2;
  if (location.pathname.startsWith("/dashboard/invoices")) ViewComponent = DashboardInvoices;
  else if (location.pathname.startsWith("/dashboard/taxes")) ViewComponent = DashboardTaxes;
  else if (location.pathname.startsWith("/dashboard/projects")) ViewComponent = DashboardProjects;
  else if (location.pathname.startsWith("/dashboard/customers")) ViewComponent = DashboardCustomers;
  else if (location.pathname.startsWith("/dashboard/time-entries")) ViewComponent = DashboardTimeEntries;

  // Filter nav items based on plan
  const navItems = allNavItems.filter(item => !item.requiresPro || isPro);

  return (
    <div className="space-y-6 font-body" data-testid="dashboard-layout">
      <ReceiptReminderBanner />
      <QuickActionsBar />

      <section className="relative overflow-hidden rounded-3xl border bg-slate-50/80 p-6 shadow-sm md:p-8">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,_rgba(148,163,184,0.18),_transparent_55%)]" />
        <div className="relative space-y-6">
          <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="space-y-2">
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-600">
                Dashboard Insights
              </span>
              <h1 className="font-display text-3xl font-semibold text-slate-900 md:text-4xl" data-testid="dashboard-title">
                Deine Finanz- und Kundenübersicht
              </h1>
              <p className="max-w-2xl text-sm text-slate-600">
                Jede Ansicht liefert fokussierte Insights – mit Filtern direkt dort, wo sie gebraucht werden.
              </p>
            </div>
          </div>

          <nav className="flex flex-wrap gap-2" data-testid="dashboard-subnav">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={item.testId}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-medium transition ${
                      isActive
                        ? "border-slate-900 bg-slate-900 text-white"
                        : "border-slate-200 bg-white text-slate-700 hover:border-slate-300"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </div>
      </section>

      <ViewComponent />
    </div>
  );
}
